import { PanelRightClose, PanelRightOpen } from "lucide-react";

/**
 * Shared tab bar + Show/Hide Document toggle for the request-detail left
 * panel. Used by the Normal (RequestDetail), NetSuite and Business Central
 * request views so all three present the same header.
 *
 * `tabs`: [{ key, label, icon, badgeCount? }]
 */
export default function RequestPanelTabs({
  tabs,
  activeTab,
  onTabChange,
  isDocViewerVisible,
  onToggleDocViewer,
}) {
  return (
    <div
      className={`flex items-center gap-2 border-gray-200 dark:border-gray-700 -mx-4 px-4 ${
        activeTab === "fields" ? "mb-3 border-b" : ""
      }`}
    >
      <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto overflow-y-hidden no-scrollbar">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              className={`group relative inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg whitespace-nowrap shrink-0 transition-all duration-200 ease-out ${
                isActive
                  ? "text-navy-700 dark:text-navy-300 bg-navy-50/70 dark:bg-navy-400/10"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              }`}
            >
              <TabIcon
                className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${
                  isActive ? "scale-110" : "group-hover:scale-105"
                }`}
              />
              {tab.label}
              {tab.badgeCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300 animate-pulse">
                  {tab.badgeCount}
                </span>
              )}
              <span
                className={`absolute left-1 right-1 -bottom-px h-0.5 rounded-full bg-navy-500 dark:bg-navy-400 transition-all duration-200 ease-out origin-center ${
                  isActive ? "opacity-100 scale-x-100" : "opacity-0 scale-x-50"
                }`}
              />
            </button>
          );
        })}
      </div>
      {onToggleDocViewer && (
        <div className="flex items-center border-l border-gray-200 dark:border-gray-700 pl-2 shrink-0">
          <button
            onClick={onToggleDocViewer}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ease-out shrink-0 shadow-sm active:scale-95 leading-none ${
              isDocViewerVisible
                ? "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow"
                : "border-navy-300 dark:border-navy-500 bg-navy-50 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300 hover:bg-navy-100 dark:hover:bg-navy-900/50 hover:shadow"
            }`}
            title={isDocViewerVisible ? "Hide document viewer" : "Show document viewer"}
          >
            <span className="relative inline-flex h-3.5 w-3.5 overflow-hidden shrink-0">
              <PanelRightClose
                className={`absolute inset-0 h-3.5 w-3.5 transition-all duration-200 ease-out ${
                  isDocViewerVisible
                    ? "opacity-100 rotate-0 scale-100"
                    : "opacity-0 -rotate-90 scale-50"
                }`}
              />
              <PanelRightOpen
                className={`absolute inset-0 h-3.5 w-3.5 transition-all duration-200 ease-out ${
                  !isDocViewerVisible
                    ? "opacity-100 rotate-0 scale-100"
                    : "opacity-0 rotate-90 scale-50"
                }`}
              />
            </span>
            <span className="hidden lg:inline whitespace-nowrap">
              {isDocViewerVisible ? "Hide Document" : "Show Document"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
